import { useMemo, useState } from 'react'
import { t } from '@lingui/core/macro'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import { arrayOf, func, string, object, bool, array, oneOfType, number, oneOf } from 'prop-types'
import Select from 'react-select'
import { useDeepCompareEffect } from 'react-use'
import { colors } from '@global/constants'
import { autocompleteComponents } from './autocomplete-components'
import { useStyles } from './autocomplete-styles'
import { Fuzzy } from '../../utils/fuse-search'

function classFriendlyStr(str) {
  return str?.split(' ')?.join('-')?.toLowerCase()
}

export const formatSelectedOptions = (isMulti, selected, options, allOptions = []) => {
  if (isMulti) {
    if (typeof selected === 'string') {
      return JSON.parse(selected)?.map(val => options?.find(opt => opt.value === val)) || []
    }
    if (Array.isArray(selected)) {
      return (
        selected.map(val => {
          // WARNING: the way we persist selected multi values is not consistent with the way react-select expects them
          // after upgrading the react-select library, this unintentional coupled behavior was revealed and was broken
          // TODO: we need to audit the usages of multi-selects and determine what translation behavior would be ideal for us to
          // prevent the need for imprecise code like the following line
          const option = options?.find(opt => opt.value == val || opt.value == val.value) // `selected` can be an array of strings or objects, but react-select expects objects that match the options it's given
          if (option) {
            return option
          }
          if (typeof val === 'string' || typeof val === 'number') {
            return { label: val, value: val }
          }
          return val
        }) || []
      )
    }
    return []
  }

  const selectedOption = allOptions.length
    ? allOptions?.find(opt => opt.value === selected)
    : options?.find(opt => opt.value === selected)

  return selectedOption || {}
}

export const CvsAutoComplete = function ReactSelect({
  selected = '',
  options = [],
  label = '',
  disabled = false,
  isMulti = false,
  noWrap = false,
  required = false,
  isClearable = false,
  showSpinner = false,
  isLoading = false,
  margin = 'normal',
  noMargin = false,
  ref = () => {},
  allOptions = [],
  maxMenuHeight = 300,
  error = '',
  onSelected = _value => {},
  styleOverrides = {},
  helperTextStyles = {},
  skin = 'light',
  allowMissingValueErrors = true,
  ...restProps
}) {
  const theme = useTheme()
  const classes = useStyles({ isMulti, noWrap, noMargin })
  const [single, setSingle] = useState(selected)
  const [multi, setMulti] = useState(selected)
  const [inputVal, setInputVal] = useState('')
  const [missingValueError, setMissingValueError] = useState(false)

  useDeepCompareEffect(() => {
    const selectedOption = formatSelectedOptions(isMulti, selected, options, allOptions)
    if (isMulti) {
      setMulti(selectedOption)
    } else {
      setSingle(selectedOption)
      allowMissingValueErrors &&
        setMissingValueError(options.length > 0 && selectedOption === undefined && selected)
    }
  }, [selected, options, isMulti, allOptions])

  const errorMessage = useMemo(() => {
    if (error) {
      return typeof error === 'string' ? error : t`Please select a field.`
    }

    if (missingValueError) {
      return t`The value for this field is missing.`
    }

    return null
  }, [error, missingValueError])

  function handleChangeSingle(value) {
    // setSingle(value);
    onSelected(value ? value.value : null)
    setInputVal('')
    setMissingValueError(false)
  }

  function handleChangeMulti(value) {
    // setMulti(value);
    onSelected((value && value.map(val => val.value)) || [])
    setInputVal('')
  }

  const selectStyles = {
    input: base => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit',
      },
      display: 'flex',
    }),
    container: base => ({
      ...base,
      '& .MuiFormControl-root': {
        margin: 0,
        ...(!disabled
          ? {
              backgroundColor: 'initial !important',
            }
          : {}),
        ...(skin === 'white'
          ? {
              backgroundColor: '#fff !important',
            }
          : {}),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        ...(errorMessage
          ? {
              borderColor: 'red !important',
            }
          : {}),
        ...(skin === 'white'
          ? {
              transition: 'border-color 250ms ease-in-out',
              border: '1px solid #E5E5E5 !important',
            }
          : {}),
      },
      '& .MuiFormLabel-root': {
        lineHeight: '1.4375em',
        ...(!isEmpty(single) || multi || inputVal
          ? {}
          : {
              transform: 'translate(14px, 50%) scale(1)',
            }),
        ...(errorMessage
          ? {
              color: `${theme.palette.error.main} !important`,
            }
          : {}),
      },
      '& .MuiTypography-body1': {
        fontSize: '13px',
      },
      ...(skin === 'white'
        ? {
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #ccc !important',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #ccc !important',
            },
          }
        : {}),
      '& .MuiOutlinedInput-input': {
        padding: '8.5px 14px',
      },
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    ...styleOverrides,
  }

  const fuzzySearch = (data, search) => {
    const fuzzy = new Fuzzy([data], { keys: ['label'] })
    return search.trim() !== '' ? fuzzy.search(search).length : true
  }

  const onFilterChange = (option, input) => (option ? fuzzySearch(option, input) : false)

  const portal = document.body

  return (
    <div
      className={
        noMargin ? classes.noMargin : margin === 'dense' ? classes.rootDense : classes.root
      }
      style={{
        backgroundColor: disabled ? colors.gray.light : 'initial',
        cursor: disabled ? 'no-drop' : 'initial',
        position: 'relative',
        ...restProps.style,
      }}
      data-test-cmp-id={`autocomplete-${restProps.name}`}
    >
      {showSpinner ? (
        <div style={{ position: 'absolute', top: 8, left: '50%' }}>
          <CircularProgress />
        </div>
      ) : null}
      <Select
        classes={classes}
        styles={selectStyles}
        isLoading={isLoading}
        inputId={`react-select-single-${classFriendlyStr(label)}-${selected}`}
        TextFieldProps={{
          margin,
          label,
          required,
          onChange: e => {
            setInputVal(e.target.value)
          },
          InputLabelProps: {
            htmlFor: `react-select-single-${label}-${selected}`,
            shrink:
              !!selected ||
              !!Object.values(single || {}).length ||
              !!(multi || []).length ||
              !!inputVal,
          },
        }}
        menuPortalTarget={portal}
        options={options}
        filterOption={onFilterChange}
        placeholder=""
        inputValue={inputVal}
        components={autocompleteComponents}
        isMulti={isMulti}
        isDisabled={disabled || showSpinner}
        isClearable={isClearable}
        maxMenuHeight={maxMenuHeight}
        menuPlacement="auto"
        value={isMulti ? multi : single}
        onChange={isMulti ? handleChangeMulti : handleChangeSingle}
        onBlur={() => {
          setInputVal('')
        }}
        ref={ref}
        error={!!errorMessage}
        {...restProps}
      />
      {errorMessage && (
        <span
          style={{
            color: colors.red.main,
            fontSize: '0.75rem',
            marginLeft: '14px',
            fontWeight: 400,
            ...helperTextStyles,
          }}
        >
          {errorMessage}
        </span>
      )}
    </div>
  )
}

CvsAutoComplete.propTypes = {
  selected: oneOfType([string, array, number]),
  onSelected: func,
  label: string,
  options: arrayOf(object),
  disabled: bool,
  isMulti: bool,
  required: bool,
  isClearable: bool,
  showSpinner: bool,
  margin: string,
  noMargin: bool,
  ref: func,
  skin: oneOf(['light', 'white']),
  allowMissingValueErrors: bool,
  error: oneOfType([string, bool]),
}
